import { useState, useEffect } from 'react';
import { Monitor, Maximize2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

type DensityMode = 'normal' | 'compact';

export function DensityToggle() {
    const { t } = useTranslation();
    const [density, setDensity] = useState<DensityMode>('normal');

    useEffect(() => {
        // Load saved density preference
        const saved = localStorage.getItem('density-mode') as DensityMode;
        if (saved) {
            setDensity(saved);
            applyDensity(saved);
        }
    }, []);

    const applyDensity = (mode: DensityMode) => {
        const scale = mode === 'compact' ? 'var(--density-compact)' : 'var(--density-normal)';
        document.documentElement.style.setProperty('--density-scale', scale);
        document.documentElement.setAttribute('data-density', mode);
    };

    const handleDensityChange = (newDensity: DensityMode) => {
        setDensity(newDensity);
        applyDensity(newDensity);
        localStorage.setItem('density-mode', newDensity);
    };

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    variant="ghost"
                    size="sm"
                    className="flex items-center gap-1 h-8 px-2"
                    title={t('header.densityTitle', { density: density === 'normal' ? t('header.densityNormal') : t('header.densityCompact') })}
                >
                    {density === 'normal' ? (
                        <Maximize2 className="h-4 w-4" />
                    ) : (
                        <Monitor className="h-4 w-4" />
                    )}
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
                <DropdownMenuItem
                    onClick={() => handleDensityChange('normal')}
                    className="flex items-center gap-2"
                >
                    <Maximize2 className="h-4 w-4" />
                    <span>{t('header.densityNormal')}</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                    onClick={() => handleDensityChange('compact')}
                    className="flex items-center gap-2"
                >
                    <Monitor className="h-4 w-4" />
                    <span>{t('header.densityCompact')}</span>
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
